import * as pc from 'playcanvas'
import type { AssetZoneController } from './assetZones'
import { type AABB, registerCollider } from './colliders'
import { worldRoot } from './world/worldRoot'
import {
  ROOM_HALF_X, ROOM_HALF_Z, WALL_HEIGHT,
  ENTRY_DOOR_HALF_WIDTH, ENTRY_DOOR_HEIGHT,
  computeWallSegments, type WallSegment,
  computeSkirtingSegments,
  COLUMN_SIZE, COLUMN_POSITIONS, SKIRTING_HEIGHT,
  CEILING_FIXTURES, FIXTURE_MOUNT_Y, FIXTURE_GLOW_Y,
  MEETING_ROOM,
  CARPET_REPEAT_M,
} from './world/layout'
import { setupOfficeDecor, spawnProp } from './world/dressing'
import {
  buildFloorPrograms,
  type HqProgramHandles,
} from './world/floorPrograms'
import { buildHqCore, type HqCoreHandles } from './world/hqCore'
import {
  applyHqFacadeReflectionProbe,
  buildHqFacade,
  type HqFacadeHandles,
} from './world/hqFacade'
import {
  buildHqLighting,
  HQ_FLOOR_MATERIAL_COLORS,
  HQ_FLOOR_MATERIAL_GLOSS,
  type HqLightingController,
} from './world/hqLighting'
import {
  HQ_STRUCTURAL_COLUMN_HEIGHT,
  HQ_STRUCTURAL_COLUMN_SIZE,
  HQ_UPPER_STRUCTURAL_COLUMNS,
  HQ_L1_COLUMN_FINISH_COLOR,
  hqStructuralColumnBounds,
} from './world/hqStructureLayout'
import { useArenaStore } from '../store'

// Palette (linear approximations) — these remain the flat-color fallback
// when a texture map fails to load.
const CREAM_COLOR    = new pc.Color(0.961, 0.922, 0.875) // #f5ebe0 walls
const CHARCOAL_COLOR = new pc.Color(0.239, 0.251, 0.357) // #3d405b trim
const CARPET_COLOR   = new pc.Color(0.42, 0.42, 0.45)    // meeting-room carpet fallback

const FLOOR_W = ROOM_HALF_X * 2 // 48
const FLOOR_D = ROOM_HALF_Z * 2 // 30

// Frosted lower band on glass walls
const GLASS_BAND_HEIGHT = 1.1

// ── Texture sets (Wave-2 assets) ──────────────────────────────────────────
// PBR jpg maps under public/assets/textures/<dir>/. Loading is best-effort:
// each material keeps its flat diffuse color until (unless) a map arrives,
// so a missing/failed texture can never crash or blank a surface.

const TEXTURES_BASE = '/assets/textures/'
/**
 * Outdoor sky. A "puresky" HDRI is deliberate: it contains nothing but sky and
 * cloud, so no photographed real-world buildings appear behind our low-poly
 * skyline. The previous studio HDRI was an INDOOR capture — outdoors it gave a
 * grey box for a sky and flat, directionless ambient with no sun.
 * Poly Haven, CC0.
 */
const ENV_HDR_URL = '/assets/env/kloofendal_48d_partly_cloudy_puresky_1k.hdr'

/**
 * Fixed golden-hour reflection source for the HQ bay glass. The visible view
 * remains the pure-sky cubemap so the authored water, boardwalk and two-depth
 * skyline are the exterior rather than being flattened against a photographed
 * city at infinity.
 */
const BAY_HDR_URL = '/assets/env/bay_sunset_2k.hdr'

// The visible pure-sky cubemap and the fixed HQ reflection cubemap are generated
// once behind the loading state.
let _defaultSkybox: pc.Texture | null = null

/**
 * Tune visible sky intensity as the player crosses the HQ threshold. The same
 * pure-sky cubemap remains visible on both sides: inside HQ, the actual bay is
 * authored geometry with near parallax and measurable altitude cues.
 */
export function setHqSkybox(app: pc.Application, inHq: boolean): void {
  if (_defaultSkybox) app.scene.skybox = _defaultSkybox
  // Dim the sky inside HQ: an interior read at full outdoor sky intensity blows
  // the whole floor out to white. The street wants the full 1.15.
  app.scene.skyboxIntensity = inHq ? 0.55 : 1.15
}

interface TextureSet {
  dir: string
  color: string
  normal?: string
  roughness?: string
}

const TEX_CARPET: TextureSet = {
  dir: 'carpet_short',
  color: 'Carpet001_1K-JPG_Color.jpg',
  normal: 'Carpet001_1K-JPG_NormalGL.jpg',
  roughness: 'Carpet001_1K-JPG_Roughness.jpg',
}
const TEX_WALL_PLASTER: TextureSet = {
  dir: 'wall_plaster_white',
  color: 'PaintedPlaster017_1K-JPG_Color.jpg',
  normal: 'PaintedPlaster017_1K-JPG_NormalGL.jpg',
  roughness: 'PaintedPlaster017_1K-JPG_Roughness.jpg',
}

type AssetLoader = {
  loadFromUrl?: (
    url: string,
    type: string,
    cb: (err: string | null, asset?: pc.Asset) => void,
  ) => void
}

function getAssetLoader(app: pc.Application): AssetLoader | null {
  const assets = (app as { assets?: AssetLoader }).assets
  if (!assets || typeof assets.loadFromUrl !== 'function') return null
  return assets
}

/**
 * Wire Color / NormalGL / Roughness maps onto a StandardMaterial with the
 * given UV tiling (repeats across the surface). Graceful: any load failure
 * leaves the flat-color material untouched.
 */
function applyTextureSet(
  app: pc.Application,
  mat: pc.StandardMaterial,
  set: TextureSet,
  tileX: number,
  tileY: number,
): void {
  const assets = getAssetLoader(app)
  if (!assets) return

  const load = (file: string, apply: (tex: pc.Texture) => void): void => {
    try {
      assets.loadFromUrl!(`${TEXTURES_BASE}${set.dir}/${file}`, 'texture', (err, asset) => {
        if (err || !asset?.resource) return
        try {
          const tex = asset.resource as pc.Texture
          tex.addressU = pc.ADDRESS_REPEAT
          tex.addressV = pc.ADDRESS_REPEAT
          tex.anisotropy = 4
          apply(tex)
          mat.update()
        } catch { /* keep the flat-color fallback */ }
      })
    } catch { /* keep the flat-color fallback */ }
  }

  load(set.color, (tex) => {
    mat.diffuseMap = tex
    mat.diffuseMapTiling = new pc.Vec2(tileX, tileY)
    mat.diffuse = new pc.Color(1, 1, 1) // let the map carry the color
  })
  if (set.normal) {
    load(set.normal, (tex) => {
      mat.normalMap = tex
      mat.normalMapTiling = new pc.Vec2(tileX, tileY)
      mat.bumpiness = 0.6
    })
  }
  if (set.roughness) {
    load(set.roughness, (tex) => {
      mat.glossMap = tex
      mat.glossMapTiling = new pc.Vec2(tileX, tileY)
      mat.glossInvert = true // roughness map → inverted gloss
      mat.gloss = 1
    })
  }
}

/**
 * HDR environment lighting: loads the studio HDR once the batch-1 (core)
 * assets are in — i.e. behind the existing loading state — then generates a
 * small env atlas + skybox via the engine EnvLighting utilities. Failures
 * fall back silently to the flat lighting rig.
 */
function setupEnvironmentLighting(
  app: pc.Application,
  hqFacade: HqFacadeHandles,
): void {
  const assets = getAssetLoader(app)
  let envLighting: typeof pc.EnvLighting | undefined
  try {
    envLighting = (pc as { EnvLighting?: typeof pc.EnvLighting }).EnvLighting
  } catch {
    envLighting = undefined // partially mocked engine without EnvLighting
  }
  if (!assets || !envLighting) return

  let started = false
  const start = (): void => {
    if (started) return
    started = true
    try {
      assets.loadFromUrl!(ENV_HDR_URL, 'texture', (err, asset) => {
        if (err || !asset?.resource) return
        try {
          const tex = asset.resource as pc.Texture
          // Downscaled lighting source keeps the one-off generation cheap.
          const lighting = envLighting.generateLightingSource(tex, { size: 128 })
          const envAtlas = envLighting.generateAtlas(lighting, {})
          lighting.destroy()
          app.scene.envAtlas = envAtlas

          // Sky from a generated cubemap, at an explicit size. Left to itself
          // the helper builds RGBA8 faces at source.width/4 — 256px LDR from a
          // 1k source, which bands visibly across an open sky and throws away
          // the HDR sun energy that makes bloom work.
          app.scene.skybox = envLighting.generateSkyboxCubemap(tex, 512)
          _defaultSkybox = app.scene.skybox
          // 0.6 was an interior value; outdoors the sky IS the light source.
          app.scene.skyboxIntensity = 1.15

          // The source equirect is no longer needed once both the atlas and
          // the cubemap are derived from it.
          tex.destroy()
        } catch { /* keep the flat lighting rig */ }

        // The HQ reflection probe is generated from a fixed-time-of-day city
        // HDRI and applied once to the single shared bay-glass material. It is
        // never installed as the visible sky, because that would erase the
        // near/far parallax authored by the P3 exterior proxy.
        try {
          assets.loadFromUrl!(BAY_HDR_URL, 'texture', (bayErr, bayAsset) => {
            if (bayErr || !bayAsset?.resource) return
            try {
              const bayTex = bayAsset.resource as pc.Texture
              const bayReflection = envLighting!.generateSkyboxCubemap(bayTex, 512)
              applyHqFacadeReflectionProbe(hqFacade, bayReflection)
              bayTex.destroy()
            } catch { /* HQ keeps the default sky */ }
          })
        } catch { /* HQ keeps the default sky */ }
      })
    } catch { /* keep the flat lighting rig */ }
  }

  try {
    if (useArenaStore.getState().assetProgress.coreReady) {
      start()
      return
    }
    const unsub = useArenaStore.subscribe((state) => {
      if (state.assetProgress.coreReady) {
        unsub()
        start()
      }
    })
    onAppDestroy(app, unsub)
  } catch { /* store unavailable — skip env lighting */ }
}

/** Register a cleanup callback on app teardown when the event API exists. */
function onAppDestroy(app: pc.Application, cb: () => void): void {
  const evts = app as unknown as { on?: (ev: string, fn: () => void) => void }
  evts.on?.('destroy', cb)
}

/**
 * Collapse the synchronous, immutable HQ shell into spatially bounded static
 * batches. These entities are direct children of the interior root; dynamic
 * Core/facade/program systems own child roots and their own batching.
 */
function batchImmediateHqShell(app: pc.Application): void {
  // The whole 36×24 m HQ is already visible from the atrium. A building-sized
  // batch bound cuts submissions without sacrificing useful culling.
  const group = app.batcher?.addGroup('hq-shell-static', false, 128)
  if (!group) return
  const root = worldRoot(app) as pc.Entity & { children?: pc.Entity[] }
  for (const child of root.children ?? []) {
    const render = child.render
    if (!render || render.batchGroupId >= 0) continue
    // Transparent guards must remain independently depth-sorted. The dynamic
    // Core is excluded structurally because it owns a child root.
    if (/^hq-core-overlook-guard-/.test(child.name)) continue
    // Hundreds of treads, rails, slabs, trims, signs, and diffuser boxes were
    // each redrawn in both sun cascades. SSAO retains their contact detail;
    // only walls and columns need to cast the large readable interior shadows.
    render.castShadows = /^(column-|wall-|ext-)/.test(child.name)
    render.batchGroupId = group.id
  }
}

function addRenderBox(
  app: pc.Application,
  name: string,
  type: 'plane' | 'box',
  pos: [number, number, number],
  scale: [number, number, number],
  mat: pc.Material,
): pc.Entity {
  const entity = new pc.Entity(name)
  entity.addComponent('render', { type })
  entity.setPosition(pos[0], pos[1], pos[2])
  entity.setLocalScale(scale[0], scale[1], scale[2])
  const meshInst = entity.render?.meshInstances[0]
  if (meshInst) meshInst.material = mat
  worldRoot(app).addChild(entity)
  return entity
}

function addBox(
  app: pc.Application,
  name: string,
  type: 'plane' | 'box',
  pos: [number, number, number],
  scale: [number, number, number],
  mat: pc.Material,
  collider: AABB,
): void {
  addRenderBox(app, name, type, pos, scale, mat)
  registerCollider(collider)
}

function makeGlassMat(alpha: number): pc.StandardMaterial {
  const mat = new pc.StandardMaterial()
  mat.diffuse = new pc.Color(0.75, 0.85, 0.90)
  mat.opacity = alpha
  mat.blendType = pc.BLEND_NORMAL
  mat.gloss = 0.9
  mat.update()
  return mat
}

/** Render one wall segment (cream box, or two-band translucent glass). */
function buildWallSegment(
  app: pc.Application,
  seg: WallSegment,
  creamMat: pc.Material,
  glassClearMat: pc.Material,
  glassFrostMat: pc.Material,
): void {
  const cx = (seg.minX + seg.maxX) / 2
  const cz = (seg.minZ + seg.maxZ) / 2
  const sx = seg.maxX - seg.minX
  const sz = seg.maxZ - seg.minZ
  const height = seg.maxY - seg.minY

  if (seg.material === 'cream') {
    addRenderBox(app, seg.id, 'box',
      [cx, seg.minY + height / 2, cz], [sx, height, sz], creamMat)
  } else {
    // Frosted lower band (alpha ~0.6) + clear upper glass (alpha ~0.25).
    addRenderBox(app, `${seg.id}-frost`, 'box',
      [cx, seg.minY + GLASS_BAND_HEIGHT / 2, cz], [sx, GLASS_BAND_HEIGHT, sz], glassFrostMat)
    addRenderBox(app, `${seg.id}-glass`, 'box',
      [cx, seg.minY + GLASS_BAND_HEIGHT + (height - GLASS_BAND_HEIGHT) / 2, cz],
      [sx, height - GLASS_BAND_HEIGHT, sz], glassClearMat)
  }

  // One collider per segment regardless of material — door gaps are simply
  // segments that don't exist.
  registerCollider({
    minX: seg.minX, maxX: seg.maxX,
    minY: seg.minY, maxY: seg.maxY,
    minZ: seg.minZ, maxZ: seg.maxZ,
    blocksSight: seg.material !== 'glass',
  })
}

export interface SceneHandles {
  /** Window fill light — colour updated by ServerRoomSystem per daemon health. */
  windowFillLight: pc.Entity
  /** Stable P7 binding seam for Core health, throughput, and run beads. */
  hqCore: HqCoreHandles
  /** Stable P7 binding seam for facade health, skyline activity, and boats. */
  hqFacade: HqFacadeHandles
  /** Stable P7 binding seam for boardroom/gallery program activity. */
  hqPrograms: HqProgramHandles
  /** Phase-6 per-floor shadowless fills and stair-height culling. */
  hqLighting: HqLightingController
}

// ── Multi-floor HQ atrium tower (Phase 1: walkable shell + grand stair) ───────
// Full spec: docs/hq-interior-spec.md. 4 floors L0..L3 (4.5 m each) + roof
// oculus; a 14×12 atrium void open the full height, offset east so the bay
// glass and the void read together. Grand switchback stair on the void's west
// edge. All geometry = boxes with AABB colliders (the FPV controller steps the
// 0.1875 m risers). Long spans are segmented for the sphere-based frustum cull.
const FLOOR_Y = [0, 4.5, 9, 13.5] as const
const SLAB_T = 0.3
const TOWER_TOP = 18
const HQ_VOID = { minX: -3, maxX: 11, minZ: -6, maxZ: 6 }
/** Three supported 0.20 m risers for the normal-FPV L3 reverse reveal. */
export const HQ_CORE_OVERLOOK_STEPS: readonly AABB[] = [
  { minX: 2.5, maxX: 5.5, minY: 13.5, maxY: 13.7, minZ: 8.0, maxZ: 8.4 },
  { minX: 2.5, maxX: 5.5, minY: 13.5, maxY: 13.9, minZ: 7.6, maxZ: 8.0 },
  { minX: 2.5, maxX: 5.5, minY: 13.5, maxY: 14.1, minZ: 6.12, maxZ: 7.6 },
] as const

/** Colliding glass completes a 1.2 m guard above the raised platform. */
export const HQ_CORE_OVERLOOK_GUARDS: readonly AABB[] = [
  { minX: 2.38, maxX: 5.62, minY: 14.6, maxY: 15.3, minZ: 6.0, maxZ: 6.12, blocksSight: false },
  { minX: 2.38, maxX: 2.5, minY: 14.1, maxY: 15.3, minZ: 6.12, maxZ: 7.6, blocksSight: false },
  { minX: 5.5, maxX: 5.62, minY: 14.1, maxY: 15.3, minZ: 6.12, maxZ: 7.6, blocksSight: false },
] as const
// Stairwell opening carved through every upper slab so the grand stair emerges
// onto each floor rather than into the slab's underside.
const HQ_STAIRWELL = { minX: -8.7, maxX: -3.0, minZ: -6.1, maxZ: 0.8 }
// Tread is wider than the player radius (0.3 m) so the foot never overlaps the
// step-after-next's floor-to-tread wall while climbing — the difference between
// walkable and unwalkable stairs for this capsule (see moveAndResolveCollisions).
const STEP = { riser: 0.1875, tread: 0.35, w: 2.4 }

function towerMat(c: pc.Color, metal: number, gloss: number): pc.StandardMaterial {
  const m = new pc.StandardMaterial()
  m.diffuse = c
  m.useMetalness = true
  m.metalness = metal
  m.gloss = gloss
  m.update()
  return m
}

export function buildTowerShell(app: pc.Application): void {
  const floorMats = [
    towerMat(new pc.Color(...HQ_FLOOR_MATERIAL_COLORS[1]), 0, HQ_FLOOR_MATERIAL_GLOSS[1]),
    towerMat(new pc.Color(...HQ_FLOOR_MATERIAL_COLORS[2]), 0, HQ_FLOOR_MATERIAL_GLOSS[2]),
    towerMat(new pc.Color(...HQ_FLOOR_MATERIAL_COLORS[3]), 0, HQ_FLOOR_MATERIAL_GLOSS[3]),
  ] as const
  const roofMat = floorMats[2]
  const railMat = towerMat(new pc.Color(0.12, 0.13, 0.16), 0.7, 0.5)
  const stepMat = towerMat(new pc.Color(0.30, 0.31, 0.34), 0.2, 0.35)
  const wallMat = towerMat(new pc.Color(0.90, 0.87, 0.80), 0, 0.1)
  const guardGlassMat = towerMat(new pc.Color(0.24, 0.54, 0.62), 0.05, 0.9)
  guardGlassMat.opacity = 0.10
  guardGlassMat.blendType = pc.BLEND_NORMAL
  guardGlassMat.depthWrite = false
  guardGlassMat.cull = pc.CULLFACE_NONE
  guardGlassMat.update()

  const slab = (name: string, minX: number, maxX: number, minZ: number, maxZ: number, walkY: number, mat: pc.Material = roofMat): void => {
    addBox(app, name, 'box',
      [(minX + maxX) / 2, walkY - SLAB_T / 2, (minZ + maxZ) / 2],
      [maxX - minX, SLAB_T, maxZ - minZ], mat,
      { minX, maxX, minY: walkY - SLAB_T, maxY: walkY, minZ, maxZ })
  }

  // ── Upper floor slabs L1..L3: a 4-band frame around the void (bands split
  // at Z=0 so no box's footprint sphere is too big to cull). ──
  for (let f = 1; f <= 3; f++) {
    const y = FLOOR_Y[f]
    const floorMat = floorMats[f - 1]
    slab(`slab${f}-e-s`, HQ_VOID.maxX, ROOM_HALF_X, -ROOM_HALF_Z, 0, y, floorMat)
    slab(`slab${f}-e-n`, HQ_VOID.maxX, ROOM_HALF_X, 0, ROOM_HALF_Z, y, floorMat)
    // West band carved around the STAIRWELL opening (X -9..-3, Z -6.2..0.9):
    // far-west is a full band; the near-west strip has the stairwell cut out.
    slab(`slab${f}-wf-s`, -ROOM_HALF_X, HQ_STAIRWELL.minX, -ROOM_HALF_Z, 0, y, floorMat)
    slab(`slab${f}-wf-n`, -ROOM_HALF_X, HQ_STAIRWELL.minX, 0, ROOM_HALF_Z, y, floorMat)
    slab(`slab${f}-wn-s`, HQ_STAIRWELL.minX, HQ_VOID.minX, -ROOM_HALF_Z, HQ_STAIRWELL.minZ, y, floorMat)
    slab(`slab${f}-wn-n`, HQ_STAIRWELL.minX, HQ_VOID.minX, HQ_STAIRWELL.maxZ, ROOM_HALF_Z, y, floorMat)
    slab(`slab${f}-s`, HQ_VOID.minX, HQ_VOID.maxX, -ROOM_HALF_Z, HQ_VOID.minZ, y, floorMat)
    slab(`slab${f}-n`, HQ_VOID.minX, HQ_VOID.maxX, HQ_VOID.maxZ, ROOM_HALF_Z, y, floorMat)

    // Void-edge balustrades (1.1 m solid parapet, so you lean over but can't fall).
    const bh = 1.1, bt = 0.12
    const bal = (name: string, minX: number, maxX: number, minZ: number, maxZ: number): void =>
      addBox(app, name, 'box',
        [(minX + maxX) / 2, y + bh / 2, (minZ + maxZ) / 2], [maxX - minX, bh, maxZ - minZ], railMat,
        { minX, maxX, minY: y, maxY: y + bh, minZ, maxZ })
    bal(`bal${f}-w-s`, HQ_VOID.minX - bt, HQ_VOID.minX, HQ_VOID.minZ, 0)
    bal(`bal${f}-w-n`, HQ_VOID.minX - bt, HQ_VOID.minX, 0, HQ_VOID.maxZ)
    bal(`bal${f}-e-s`, HQ_VOID.maxX, HQ_VOID.maxX + bt, HQ_VOID.minZ, 0)
    bal(`bal${f}-e-n`, HQ_VOID.maxX, HQ_VOID.maxX + bt, 0, HQ_VOID.maxZ)
    bal(`bal${f}-s-w`, HQ_VOID.minX, 4, HQ_VOID.minZ - bt, HQ_VOID.minZ)
    bal(`bal${f}-s-e`, 4, HQ_VOID.maxX, HQ_VOID.minZ - bt, HQ_VOID.minZ)
    bal(`bal${f}-n-w`, HQ_VOID.minX, 4, HQ_VOID.maxZ, HQ_VOID.maxZ + bt)
    bal(`bal${f}-n-e`, 4, HQ_VOID.maxX, HQ_VOID.maxZ, HQ_VOID.maxZ + bt)
  }

  // The raised overlook stays wholly on the supported L3 band. The original
  // 1.1 m solid parapet remains unchanged; glass guards restore a 1.2 m total
  // barrier above the platform without hiding the full-height shaft.
  for (const [index, box] of HQ_CORE_OVERLOOK_STEPS.entries()) {
    addBox(app, `hq-core-overlook-step-${index + 1}`, 'box',
      [(box.minX + box.maxX) / 2, (box.minY + box.maxY) / 2, (box.minZ + box.maxZ) / 2],
      [box.maxX - box.minX, box.maxY - box.minY, box.maxZ - box.minZ],
      stepMat, box)
  }
  for (const [index, box] of HQ_CORE_OVERLOOK_GUARDS.entries()) {
    addBox(app, `hq-core-overlook-guard-${index + 1}`, 'box',
      [(box.minX + box.maxX) / 2, (box.minY + box.maxY) / 2, (box.minZ + box.maxZ) / 2],
      [box.maxX - box.minX, box.maxY - box.minY, box.maxZ - box.minZ],
      guardGlassMat, box)
  }

  // ── Grand switchback stair, L0→L1→L2→L3, on the void's west edge. Each step
  // is a solid box from the floor up to its tread; one AABB per step so the FPV
  // stepper catches every 0.1875 m riser. ──
  const xcA = -7.3, xcB = -4.3
  for (let s = 0; s < 3; s++) {
    const fromY = FLOOR_Y[s]
    for (let i = 0; i < 12; i++) {
      const top = fromY + (i + 1) * STEP.riser
      const zc = -6 + (i + 0.5) * STEP.tread
      addBox(app, `stairA-${s}-${i}`, 'box',
        [xcA, (fromY + top) / 2, zc], [STEP.w, top - fromY, STEP.tread], stepMat,
        { minX: xcA - STEP.w / 2, maxX: xcA + STEP.w / 2, minY: fromY, maxY: top, minZ: zc - STEP.tread / 2, maxZ: zc + STEP.tread / 2 })
    }
    // Flight A ends near Z = -6 + 12*tread; the landing spans BOTH lanes so you
    // turn 180° from flight A (west lane) onto flight B (east lane).
    const landY = fromY + 12 * STEP.riser
    const landZ0 = -6 + 12 * STEP.tread
    addBox(app, `stairLand-${s}`, 'box',
      [-5.8, landY - SLAB_T / 2, landZ0 + 1.2], [5.4, SLAB_T, 2.4], stepMat,
      { minX: -8.5, maxX: -3.1, minY: landY - SLAB_T, maxY: landY, minZ: landZ0, maxZ: landZ0 + 2.4 })
    // 11 steps, not 12: the 12th tread would land exactly at arrY, coplanar with
    // the arrival landing slab below (same height, same material) — that pair
    // z-fights. The landing IS the final tread; the player steps onto it from
    // step 10 (one riser below).
    for (let i = 0; i < 11; i++) {
      const top = landY + (i + 1) * STEP.riser
      const zc = landZ0 - (i + 0.5) * STEP.tread
      addBox(app, `stairB-${s}-${i}`, 'box',
        [xcB, (landY + top) / 2, zc], [STEP.w, top - landY, STEP.tread], stepMat,
        { minX: xcB - STEP.w / 2, maxX: xcB + STEP.w / 2, minY: landY, maxY: top, minZ: zc - STEP.tread / 2, maxZ: zc + STEP.tread / 2 })
    }

    // Floor-level arrival landing: bridges flight B's top to the next flight and
    // to the west floor band (wn-s ends at Z=-6.1), so the switchback turn has
    // solid ground at every storey instead of a gap you fall through. It must
    // NOT reach north past the top steps (Z=-5.65) — a slab over the still-
    // climbing lower steps becomes a ceiling your torso hits mid-climb.
    const arrY = FLOOR_Y[s + 1]
    addBox(app, `stairFloorLand-${s}`, 'box',
      [-5.8, arrY - SLAB_T / 2, -5.875], [5.4, SLAB_T, 0.45], stepMat,
      { minX: -8.5, maxX: -3.1, minY: arrY - SLAB_T, maxY: arrY, minZ: -6.1, maxZ: -5.65 })

    // Stepped side balustrades (handrails) down both flights: guard the open
    // outer sides AND the gap between the two lanes, so you can't fall off the
    // stair. 1 m tall (> MAX_STEP) so they block rather than get climbed.
    // skipChunk drops the balustrade chunk nearest the arrival floor on the two
    // INNER edges, so the floor-level path between flight B's top and flight A's
    // base (the switchback turn on the floor) stays open — otherwise the inner
    // rails wall you off from the next flight.
    const RAIL_H = 1.0, rt = 0.14
    const railFlight = (tag: string, xEdge: number, zStart: number, zDir: 1 | -1, baseY: number, skipChunk = -1): void => {
      for (let c = 0; c < 4; c++) {
        if (c === skipChunk) continue
        const zA = zStart + zDir * (c * 3) * STEP.tread
        const zB = zStart + zDir * (c * 3 + 3) * STEP.tread
        const zMin = Math.min(zA, zB), zMax = Math.max(zA, zB)
        const lowY = baseY + c * 3 * STEP.riser
        const topY = baseY + (c * 3 + 3) * STEP.riser + RAIL_H
        addBox(app, `${tag}-${c}`, 'box',
          [xEdge, (lowY + topY) / 2, (zMin + zMax) / 2], [rt, topY - lowY, zMax - zMin], railMat,
          { minX: xEdge - rt / 2, maxX: xEdge + rt / 2, minY: lowY, maxY: topY, minZ: zMin, maxZ: zMax })
      }
    }
    railFlight(`railA-${s}-w`, xcA - STEP.w / 2, -6, 1, fromY)
    railFlight(`railA-${s}-e`, xcA + STEP.w / 2, -6, 1, fromY, 0)
    railFlight(`railB-${s}-w`, xcB - STEP.w / 2, landZ0, -1, landY, 3)
    railFlight(`railB-${s}-e`, xcB + STEP.w / 2, landZ0, -1, landY)

    // Landing balustrade: rail the three open edges of the switchback landing.
    // Only the south side is left open, where the two flights connect to it.
    const lz1 = landZ0 + 2.4
    const landRail = (tag: string, minX: number, maxX: number, minZ: number, maxZ: number): void =>
      addBox(app, tag, 'box',
        [(minX + maxX) / 2, landY + RAIL_H / 2, (minZ + maxZ) / 2], [maxX - minX, RAIL_H, maxZ - minZ], railMat,
        { minX, maxX, minY: landY, maxY: landY + RAIL_H, minZ, maxZ })
    landRail(`landRail-${s}-n`, -8.5, -3.1, lz1 - rt, lz1)
    landRail(`landRail-${s}-w`, -8.5, -8.5 + rt, landZ0, lz1)
    landRail(`landRail-${s}-e`, -3.1 - rt, -3.1, landZ0, lz1)
  }

  // ── Full-height exterior shell above L0 (the L0 office keeps its own 3 m
  // walls/glass; this carries the tower up to the roof). Segmented for culling. ──
  const wy0 = 3
  const extWall = (name: string, minX: number, maxX: number, minZ: number, maxZ: number, mat: pc.Material): void =>
    addBox(app, name, 'box',
      [(minX + maxX) / 2, (wy0 + TOWER_TOP) / 2, (minZ + maxZ) / 2],
      [maxX - minX, TOWER_TOP - wy0, maxZ - minZ], mat,
      { minX, maxX, minY: wy0, maxY: TOWER_TOP, minZ, maxZ })
  for (let z = -ROOM_HALF_Z; z < ROOM_HALF_Z; z += 6) {
    extWall(`ext-w-${z}`, -ROOM_HALF_X - 0.3, -ROOM_HALF_X, z, z + 6, wallMat)
  }
  for (let x = -ROOM_HALF_X; x < ROOM_HALF_X; x += 6) {
    extWall(`ext-n-${x}`, x, x + 6, ROOM_HALF_Z, ROOM_HALF_Z + 0.3, wallMat)
    extWall(`ext-s-${x}`, x, x + 6, -ROOM_HALF_Z - 0.3, -ROOM_HALF_Z, wallMat)
  }
  // ── Roof cap (4-band frame around an 8×7 oculus over the void; glazed in P2). ──
  slab('roof-e', 8, ROOM_HALF_X, -ROOM_HALF_Z, ROOM_HALF_Z, TOWER_TOP)
  slab('roof-w', -ROOM_HALF_X, 0, -ROOM_HALF_Z, ROOM_HALF_Z, TOWER_TOP)
  slab('roof-s', 0, 8, -ROOM_HALF_Z, -3.5, TOWER_TOP)
  slab('roof-n', 0, 8, 3.5, ROOM_HALF_Z, TOWER_TOP)
}

export function setupScene(
  app: pc.Application,
  assetZones?: AssetZoneController<pc.Asset>,
): SceneHandles {
  // ── Way out ───────────────────────────────────────────────────────────────
  // The entry gap read as a hole onto nothing. A lit frame and an EXIT sign
  // make it obvious this is the way back to the street, which is the one piece
  // of navigation an interior genuinely needs.
  const exitFrameMat = new pc.StandardMaterial()
  exitFrameMat.diffuse = new pc.Color(0.10, 0.11, 0.13)
  exitFrameMat.useMetalness = true
  exitFrameMat.metalness = 0.5
  exitFrameMat.gloss = 0.5
  exitFrameMat.update()

  const exitGlowMat = new pc.StandardMaterial()
  exitGlowMat.diffuse = new pc.Color(0.10, 0.85, 0.45)
  exitGlowMat.emissive = new pc.Color(0.20, 1.0, 0.55)
  exitGlowMat.emissiveIntensity = 14
  exitGlowMat.useLighting = false
  exitGlowMat.update()

  const exitZ = ROOM_HALF_Z - 0.08
  const jamb = 0.16
  for (const side of [-1, 1]) {
    addRenderBox(app, `exit-jamb-${side}`, 'box',
      [side * (ENTRY_DOOR_HALF_WIDTH + jamb / 2), ENTRY_DOOR_HEIGHT / 2, exitZ],
      [jamb, ENTRY_DOOR_HEIGHT, 0.2], exitFrameMat)
  }
  addRenderBox(app, 'exit-head', 'box',
    [0, ENTRY_DOOR_HEIGHT + jamb / 2, exitZ],
    [ENTRY_DOOR_HALF_WIDTH * 2 + jamb * 2, jamb, 0.2], exitFrameMat)
  // Running-man green, the universal "way out" colour.
  addRenderBox(app, 'exit-sign', 'box',
    [0, ENTRY_DOOR_HEIGHT + 0.42, exitZ - 0.06],
    [0.9, 0.26, 0.06], exitGlowMat)

  // ── Interior backdrop ─────────────────────────────────────────────────────
  // The interior sits on its own island with nothing around it, so any gap in
  // the shell — chiefly the entry doorway — used to look out onto bare sky.
  // A dark shell just outside the room closes every sightline without needing
  // to model a whole street on the island.
  const backdropMat = new pc.StandardMaterial()
  backdropMat.diffuse = new pc.Color(0.06, 0.065, 0.075)
  backdropMat.useLighting = false
  backdropMat.cull = pc.CULLFACE_NONE
  backdropMat.update()

  const BACKDROP_PAD = 6
  const BACKDROP_H = WALL_HEIGHT + 6
  const bx = ROOM_HALF_X + BACKDROP_PAD
  const bz = ROOM_HALF_Z + BACKDROP_PAD
  // Three sides keep the dark shell; the EAST side is left open as the HQ's
  // bay-view frontage. There is deliberately no legacy backdrop lid: the real
  // four-band tower roof closes the building while its oculus keeps the full
  // Y 0→18 atrium and sky reveal open.
  addRenderBox(app, 'backdrop-north', 'box', [0, BACKDROP_H / 2, -bz], [bx * 2, BACKDROP_H, 0.4], backdropMat)
  addRenderBox(app, 'backdrop-south', 'box', [0, BACKDROP_H / 2, bz], [bx * 2, BACKDROP_H, 0.4], backdropMat)
  addRenderBox(app, 'backdrop-west', 'box', [-bx, BACKDROP_H / 2, 0], [0.4, BACKDROP_H, bz * 2], backdropMat)

  addRenderBox(app, 'backdrop-floor', 'plane', [0, -0.05, 0], [bx * 2, 1, bz * 2], backdropMat)

  // ── Aerial perspective ────────────────────────────────────────────────────
  // Distance haze, and the thing that hides the far edge of the district
  // instead of letting geometry pop at the far plane. The colour must match
  // the sky's horizon or you get a grey band where the fog meets an unfogged
  // skybox — the skybox is never fogged.
  if (app.scene.fog) {
    app.scene.fog.type = pc.FOG_LINEAR
    // Matched to the HDRI's measured horizon radiance. The old colour was
    // brighter and BLUER than the sky, so distance faded to a pale halo
    // lighter than the sky behind it. The 70-260 range also did nothing: the
    // district is only ~90 m across, so peak fog was about 10%.
    app.scene.fog.color = new pc.Color(0.62, 0.66, 0.74)
    app.scene.fog.start = 25
    app.scene.fog.end = 150
  }

  // ── Ambient ───────────────────────────────────────────────────────────────
  // NB: this is only in effect until the HDR envAtlas loads — once
  // scene.envAtlas is set the standard material switches its ambient source to
  // the atlas and ignores scene.ambientLight entirely. Kept as the pre-IBL
  // look so the first frames aren't black.
  // Dark-cool ambient is the stable baseline. The upward brightness arc comes
  // from floor albedo plus bounded local fills, not from washing every floor.
  app.scene.ambientLight = new pc.Color(0.078, 0.102, 0.133)

  // Clustered lighting is ON — it is the engine-only DEFAULT (scene.js), not
  // something we opt into; only the Editor's scene-settings path defaults it
  // off. So all point/spot lights here ARE clustered. What we trim below are
  // the clustered FEATURES we don't use: each enabled feature bloats every
  // compiled shader and slows first-frame compilation. Directional shadows
  // never use the clustered path, which is why the sun still casts.
  if (app.scene.lighting) {
    app.scene.lighting.shadowsEnabled = false
    app.scene.lighting.cookiesEnabled = false
    app.scene.lighting.areaLightsEnabled = false
  }

  // ── L0 floor: dark stone anchors the bottom of the upward material arc. ──
  const floorMat = new pc.StandardMaterial()
  floorMat.diffuse = new pc.Color(...HQ_FLOOR_MATERIAL_COLORS[0])
  floorMat.useMetalness = true
  floorMat.metalness = 0
  floorMat.gloss = HQ_FLOOR_MATERIAL_GLOSS[0]
  floorMat.update()

  addBox(app, 'floor', 'plane',
    [0, 0, 0], [FLOOR_W, 1, FLOOR_D], floorMat,
    { minX: -ROOM_HALF_X, maxX: ROOM_HALF_X, minY: -1, maxY: 0, minZ: -ROOM_HALF_Z, maxZ: ROOM_HALF_Z },
  )

  // ── Meeting-room carpet (short carpet, ~1.5 m repeat) ────────────────────
  // A thin walkable patch laid over the wood floor, inset from the glass.
  const carpetMat = new pc.StandardMaterial()
  carpetMat.diffuse = CARPET_COLOR
  carpetMat.useMetalness = true
  carpetMat.metalness = 0
  carpetMat.gloss = 0.04
  carpetMat.update()
  const carpetW = MEETING_ROOM.maxX - MEETING_ROOM.minX - 0.3
  const carpetD = MEETING_ROOM.maxZ - MEETING_ROOM.minZ - 0.3
  applyTextureSet(app, carpetMat, TEX_CARPET,
    carpetW / CARPET_REPEAT_M, carpetD / CARPET_REPEAT_M)

  addRenderBox(app, 'meeting-carpet', 'plane',
    [(MEETING_ROOM.minX + MEETING_ROOM.maxX) / 2, 0.005, (MEETING_ROOM.minZ + MEETING_ROOM.maxZ) / 2],
    [carpetW, 1, carpetD], carpetMat)

  // ── Walls from the floor-plan wall segments ───────────────────────────────
  // Exterior shell (with the south entry gap), meeting-room glass box and
  // server-room partitions all come from computeWallSegments — the door gaps
  // are missing segments, so the collider registry inherits them for free.
  const wallMat = new pc.StandardMaterial()
  wallMat.diffuse = CREAM_COLOR
  wallMat.useMetalness = true
  wallMat.metalness = 0
  wallMat.gloss = 0.15
  wallMat.update()
  // White plaster on all solid walls. Box UVs span 0-1 per face, so one
  // shared tiling is an approximation of real-world repeat (documented).
  applyTextureSet(app, wallMat, TEX_WALL_PLASTER, 3, 1.5)

  const glassClearMat = makeGlassMat(0.25)
  const glassFrostMat = makeGlassMat(0.6)

  for (const seg of computeWallSegments()) {
    // The P3 facade owns the east frontage across every floor. Rendering this
    // legacy L0 segment as well would stack two transparent panes.
    if (seg.id === 'wall-east') continue
    buildWallSegment(app, seg, wallMat, glassClearMat, glassFrostMat)
  }

  // ── Charcoal skirting along every solid wall base (covers floor seams) ───
  const skirtMat = new pc.StandardMaterial()
  skirtMat.diffuse = CHARCOAL_COLOR
  skirtMat.useMetalness = true
  skirtMat.metalness = 0.6
  skirtMat.gloss = 0.45
  skirtMat.update()

  for (const sk of computeSkirtingSegments()) {
    addRenderBox(app, sk.id, 'box',
      [(sk.minX + sk.maxX) / 2, sk.height / 2, (sk.minZ + sk.maxZ) / 2],
      [sk.maxX - sk.minX, sk.height, sk.maxZ - sk.minZ], skirtMat)
  }

  // ── Multi-floor tower shell ──────────────────────────────────────────────
  // The old single 3 m ceiling is gone: L0 is now ceilinged by the L1 slab and
  // opens into the full-height atrium void over the centre. buildTowerShell
  // adds the upper floors, the grand stair, balustrades and the tower's outer
  // shell (see docs/hq-interior-spec.md, Phase 1).
  buildTowerShell(app)
  const hqCore = buildHqCore(app)
  const hqFacade = buildHqFacade(app)

  // Furnish the upper floors (bay lounges, void-corner plants, L2/L3 partition
  // offices). Live-run agent desks are placed separately by StationSystem, now
  // spread across all floors via the floor-aware slot set in floorPrograms.
  const hqPrograms = buildFloorPrograms(app, assetZones)

  // The east frontage, its height proxies, and P7 projection seams are owned by
  // hqFacade. The shared scene skybox and KEY_SUN remain singletons.

  // ── Structural columns ───────────────────────────────────────────────────
  // Full-height, solid, collidable. They read as structure, break long
  // sightlines, and give the eye a human-scale reference in open floor.
  // Dark steel columns, not blank cream monoliths: they were the single
  // ugliest thing in the room — full-height white blocks that read as unfinished
  // pillars. Charcoal + a metallic sheen makes them intentional structure that
  // recedes against the bright glazed floor.
  const columnMat = new pc.StandardMaterial()
  columnMat.diffuse = CHARCOAL_COLOR
  columnMat.useMetalness = true
  columnMat.metalness = 0.55
  columnMat.gloss = 0.55
  columnMat.update()
  const l1ColumnMat = new pc.StandardMaterial()
  l1ColumnMat.diffuse = new pc.Color(...HQ_L1_COLUMN_FINISH_COLOR)
  l1ColumnMat.useMetalness = true
  l1ColumnMat.metalness = 0.32
  l1ColumnMat.gloss = 0.4
  l1ColumnMat.update()

  const colHalf = COLUMN_SIZE / 2
  for (const [cx, cz] of COLUMN_POSITIONS) {
    addBox(app, `column-${cx}-${cz}`, 'box',
      [cx, WALL_HEIGHT / 2, cz], [COLUMN_SIZE, WALL_HEIGHT, COLUMN_SIZE], columnMat,
      {
        minX: cx - colHalf, maxX: cx + colHalf,
        minY: 0, maxY: WALL_HEIGHT,
        minZ: cz - colHalf, maxZ: cz + colHalf,
      },
    )
    // Skirting return so the column meets the floor like built work.
    addRenderBox(app, `column-skirt-${cx}-${cz}`, 'box',
      [cx, SKIRTING_HEIGHT / 2, cz],
      [COLUMN_SIZE + 0.06, SKIRTING_HEIGHT, COLUMN_SIZE + 0.06], skirtMat)
  }

  // Upper-storey structure follows the master 6 m grid. The pure layout owns
  // all void/stair/circulation omissions, so render and collision geometry can
  // never drift apart. Each column stops at the next slab underside (4.2 m)
  // and remains an independent storey segment before the shell batch is built.
  for (const column of HQ_UPPER_STRUCTURAL_COLUMNS) {
    const bounds = hqStructuralColumnBounds(column)
    addBox(
      app,
      `column-grid-l${column.floor}-x${column.x}-z${column.z}`,
      'box',
      [
        column.x,
        column.walkY + HQ_STRUCTURAL_COLUMN_HEIGHT / 2,
        column.z,
      ],
      [
        HQ_STRUCTURAL_COLUMN_SIZE,
        HQ_STRUCTURAL_COLUMN_HEIGHT,
        HQ_STRUCTURAL_COLUMN_SIZE,
      ],
      column.floor === 1 ? l1ColumnMat : columnMat,
      bounds,
    )
  }

  // ── Warm directional sun (key light) ─────────────────────────────────────
  const sun = new pc.Entity('KEY_SUN')
  sun.addComponent('light', {
    type: 'directional',
    color: new pc.Color(1.0, 0.95, 0.80),
    intensity: 1.15,
    castShadows: true,
    // Two cascades over the district/HQ range: four doubled every structural
    // caster again without a visible gain at walking distance.
    // only 36x24m, so this roughly quadruples texel density where the player
    // actually is. shadowBias was 4x the engine default, which detached
    // shadows from their casters (peter-panning); normalOffsetBias is the
    // correct acne fix and was never set.
    numCascades: 2,
    cascadeDistribution: 0.9,
    cascadeBlend: 0.1,
    shadowDistance: 70,
    shadowResolution: 2048,
    shadowType: pc.SHADOW_PCF5_16F,
    shadowBias: 0.05,
    normalOffsetBias: 0.05,
    shadowIntensity: 0.85,
  })
  // 45 deg is noon — flat and short-shadowed. A lower sun raking ACROSS the
  // street throws long building shadows over the carriageway, which is the
  // strongest single cue that a space is outdoors.
  // A directional light's direction is -worldTransform.getY(), so ELEVATION =
  // 90 - eulerX. setEulerAngles(32, ...) therefore gave a 58 deg near-noon sun
  // — HIGHER and flatter than the 45 deg it was written to escape. A 14 m
  // building cast 8.7 m of shadow instead of crossing the 7.5 m carriageway.
  // 60 here is a 30 deg sun raking from the south-west: ~24 m of shadow.
  sun.setEulerAngles(60, -25, 0)
  worldRoot(app).addChild(sun)

  // ── Window fill light (tinted by daemon health via ServerRoomSystem) ──────
  const fillLight = new pc.Entity('windowFill')
  fillLight.addComponent('light', {
    type: 'point',
    color: new pc.Color(0.92, 0.89, 0.80),
    // Gentle warm fill — the bright outdoor IBL floods the glass side, so this
    // only lifts the deep-floor shadows away from the window without blowing the
    // room out to white (a strong fill did exactly that).
    intensity: 0.55,
    range: 12,
    castShadows: false,
  })
  fillLight.setPosition(0, 3.2, 0)
  worldRoot(app).addChild(fillLight)

  // ── Ceiling fixtures: one visible source per legacy L0 zone. Actual local
  // illumination belongs to the bounded Phase-6 rig below; keeping a point
  // light on every decorative fixture was the source of the unculled light
  // explosion. The emissive diffuser still renders if its GLB is missing.
  const fixtureGlowMat = new pc.StandardMaterial()
  fixtureGlowMat.diffuse = CHARCOAL_COLOR
  fixtureGlowMat.emissive = new pc.Color(1.0, 0.92, 0.78)
  fixtureGlowMat.emissiveIntensity = 25
  fixtureGlowMat.useLighting = false
  fixtureGlowMat.update()

  for (const f of CEILING_FIXTURES) {
    // GLB fixture detail (best-effort; the loader no-ops if the prop fails)
    spawnProp(app, 'prop_lightCeiling.glb', [f.x, FIXTURE_MOUNT_Y, f.z])

    // Emissive diffuser panel — the guaranteed-visible fixture
    const glow = new pc.Entity(`fixture-${f.id}`)
    glow.addComponent('render', { type: 'box' })
    glow.setLocalScale(0.55, 0.06, 0.55)
    glow.setPosition(f.x, FIXTURE_GLOW_Y, f.z)
    const gmi = glow.render?.meshInstances[0]
    if (gmi) gmi.material = fixtureGlowMat
    worldRoot(app).addChild(glow)

  }

  // ── HDR environment lighting (deferred behind the loading state) ─────────
  setupEnvironmentLighting(app, hqFacade)

  // ── Static office décor (meeting room, reception, plants, whiteboard) ────
  setupOfficeDecor(app)

  const hqLighting = buildHqLighting(app)
  batchImmediateHqShell(app)

  return { windowFillLight: fillLight, hqCore, hqFacade, hqPrograms, hqLighting }
}
